import React from "react";
import { withRouter } from "react-router-dom";
import { connect } from "react-redux";
import * as actions from "../../actions";
import { Form, Input, Button, Row, Col, Icon, Spin, Checkbox, Steps } from "antd";
import { Country, State, City } from "country-state-city";
import countryList from "react-select-country-list";
import { v4 as uuidv4 } from "uuid";
import Select from "react-select";
import "./CargoInsurance.css";
import {
  INSURANCE_PROFIT_PERCENT,
  extractPremium,
  profitAmount,
  isQuoteCreated,
  hasInstantPremium,
} from "../../util/insurancePremium";

export { INSURANCE_PROFIT_PERCENT };

const { Step } = Steps;

// react-select wants {value,label} for the category field. A manually-picked
// category stores the commodity ID (selectedOption.value === item.id), so on a
// remount (after a PO import bumps importEpoch) the initialValue must resolve the
// human label from commodityList by ID — otherwise the field renders the raw ID as
// its label (#5). Returns undefined when no category is set or it can't be resolved.
export const resolveCategoryOption = (categoryId, commodityList) => {
  if (categoryId == null || categoryId === "") return undefined;
  const match = (commodityList || []).find((c) => c.id === categoryId);
  return match
    ? { value: match.id, label: match.commodity_type }
    : { value: categoryId, label: categoryId };
};

// Serialize the commodity inputs a quote is priced on (category / description /
// value). Comparing this across renders detects a post-Calculate edit so stale
// quotes can be invalidated before they reach Create Policy — a charged action
// (a premium priced on the OLD value/category must never be the one billed).
export const commodityContentKey = (commodityArr) =>
  JSON.stringify(
    (Array.isArray(commodityArr) ? commodityArr : []).map((c) => [
      c?.category,
      c?.description,
      c?.commodityValue,
    ])
  );

const labelStyle = {
  fontSize: "16px",
  width: "100%",
  textAlign: "left",
  whiteSpace: "normal",
  display: "inline-flex",
};

const customStyles = {
  control: (baseStyles, state) => ({
    ...baseStyles,
    padding: 0,
    margin: 0,
  }),
  option: (provided) => ({
    ...provided,
    padding: "0 10px", // Remove padding
    margin: 0, // Remove margin
  }),
  menu: (provided) => ({
    ...provided,
    margin: 0, // Remove margin
    padding: 0, // Remove padding
  }),
  menuList: (provided) => ({
    ...provided,
    padding: 0, // Remove padding
  }),
  singleValue: (provided) => ({
    ...provided,
    margin: 0, // Remove margin
    padding: 0, // Remove padding
  }),
  placeholder: (provided) => ({
    ...provided,
    margin: 0, // Remove margin
    padding: 0, // Remove padding
  }),
  input: (provided) => ({
    ...provided,
    margin: 0,
    padding: 0,
  }),
};

class CargoInsurance extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      loading: false,
      loadingPolicy: false,
      CountryISOEnum: {},
      authToken: null,
      category: {},
      insurance: [],
      shouldValidate: false,
      loadingShouldPromptForLocation: false,
      // The company-location fields are only needed to GET A QUOTE / CREATE A POLICY
      // (Breeze requires a valid entity address). Insurance is optional and never gates
      // the booking, so the required-field errors stay hidden until the customer
      // actually attempts to Calculate — flipped true in updateEntityLocation on a
      // missing-field attempt, cleared once a valid location is saved.
      locationValidateAttempted: false,
      // Customer must explicitly authorize the charge before a policy (which we pay
      // Breeze for and bill the customer) can be created. Reset on every re-calculate
      // so the tick always matches the amount currently shown.
      policyAuthorized: false,
    };
  }

  /**
   * The checkUserEntity function calls the backend to see if the user's entity complies with the Breeze API
   */
  async checkUserEntity() {
    this.setState({ loadingShouldPromptForLocation: true });
    const {
      queryParams,
      final_search_form,
      commodityList,
      insuranceToken,
      user,
      booking_customer_form,
    } = this.props;

    // Check if userId exists and is not null/undefined
    const isUserPresent = user.webuser?.userid != null;
    const isUserEntityValid = await this.props.validateUserEntityForBreezeApiFromApi(isUserPresent);
    if (isUserEntityValid) {
      console.log("User entity is valid for Breeze API");
    } else {
      console.warn("User entity is not valid");
    }

    this.setState({ shouldPromptForLocation: isUserPresent && !isUserEntityValid });
    this.setState({ loadingShouldPromptForLocation: false });
  }

  componentDidMount(){
    this.checkUserEntity();
  }

  // Editing any commodity input after a Calculate invalidates the existing quotes
  // (they were priced on the previous values/categories). Clear them + the
  // authorization and notify the parent so the stale premium can be neither shown
  // nor used to Create Policy — the customer must re-Calculate. Mirrors
  // RfqInsuranceCard's content-key invalidation. Guarded so it only fires on a real
  // content change while quotes exist (no render loop, no-op once cleared).
  componentDidUpdate(prevProps) {
    if (
      this.state.insurance.length > 0 &&
      commodityContentKey(prevProps.commodityArr) !==
        commodityContentKey(this.props.commodityArr)
    ) {
      this.setState({ insurance: [], policyAuthorized: false });
      this.props.onInsuranceAmountChange([]);
    }
  }

  findISOCodeByCountryName = (countryName) => {
    //console.log("countryName: ", countryName);
    if (countryName === "USA") {
      countryName = "United States";
    }
    const list = countryList().getData();
    //console.log("list: ", list);
    const country = list.find((c) => c.label === countryName);
    return country ? country.value : null;
  };

  // Function to remove accents/diacritics from a string
  removeAccents = (str) => {
    return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  };

  // Function to normalize city names (convert to lowercase and remove accents)
  normalizeCityName = (cityName) => {
    return this.removeAccents(cityName).toLowerCase();
  };

  findStateCodeByCityAndCountryName = (cityName, countryName) => {
    let cityFound = null;
    if (countryName === "Canada") {
      // First, find the country code based on the country name
      const country = Country.getAllCountries().find(
        (c) => c.name === countryName
      );
      if (!country) return null; // Country not found
      // console.log("country: ", country, country.isoCode);
      // Then, find all states within this country
      const states = State.getStatesOfCountry(country.isoCode);
      // console.log("states: ", states);
      // Now, find the city by name within the found states

      for (let state of states) {
        const cities = City.getCitiesOfState(country.isoCode, state.isoCode);
        const city = cities.find(
          (c) =>
            this.normalizeCityName(c.name) === this.normalizeCityName(cityName)
        );
        // console.log("cities: ", cities, "city: ", city);
        if (city) {
          cityFound = city;
          break;
        }
      }
    }
    // Return the state code of the found city, or null if not found
    return cityFound ? cityFound.stateCode : null;
  };

  handleSubmit = async (values) => {
    const {
      queryParams,
      final_search_form,
      commodityList,
      insuranceToken,
      user,
      booking_customer_form,
    } = this.props;

    // Check if userId exists and is not null/undefined
    const isUserPresent = user.webuser?.userid != null;

    // address fields required for Breeze: full address, country and country code
    const fullAddress = [booking_customer_form?.street, booking_customer_form?.address].filter(Boolean).join(", ");

    const customer = {
      address: fullAddress,
      companyCode: booking_customer_form?.company_name,
      companyName: booking_customer_form?.company_name,
      countryCode: this.findISOCodeByCountryName(
        booking_customer_form?.country
      ),
      country: booking_customer_form?.country,
    };

    //console.log("customer: ",customer)
    const commodities = values?.commodityArr || [];

    let commodityObj = {
      currency_code: "USD",
      container_mode:
        final_search_form?.type === "api-fcl"
          ? "FCL"
          : final_search_form?.type === "api-lcl"
          ? "LCL"
          : null,
      origin_port_code: queryParams?.from?.portCode,
      // destination_port_code:
      //   final_search_form?.selected_card_json?.port_of_discharge?.scac_code,
      destination_port_code: queryParams?.to?.portCode,
      etd: final_search_form?.selected_card_json?.etd_origin,
      primary_mode_of_transport:
        final_search_form?.selected_card_json?.mode.toLowerCase(),
    };

    if (commodities && commodities.length > 0) {
      this.setState({ loading: true });
      const insuranceResults = await this.processCommodities(
        commodities,
        commodityObj,
        insuranceToken,
        customer,
        isUserPresent
      );
      this.setState({ loading: false });
      if (insuranceResults && insuranceResults.length > 0) {
        this.setState({ insurance: insuranceResults });
      }
      //console.log("Insurance Results: ", insuranceResults);
      this.props.onInsuranceAmountChange(insuranceResults);
    } else {
      console.log("Error in fetching form values");
      this.setState({ loading: false });
    }
  };

  processCommodities = async (
    commodities,
    commodityBase,
    insuranceToken,
    customer,
    isUserPresent
  ) => {
    return Promise.all(
      commodities.map(async (commodity) => {
        const commodityObj = this.createCommodityObject(
          commodity,
          commodityBase
        );

        try {
          const result = await this.props.getInsuranceQuoteFromAPI(
            isUserPresent,
            insuranceToken,
            commodityObj,
            customer
          );
          // console.log("Result: ", result);
          return this.handleInsuranceResult(result, commodityObj);
        } catch (error) {
          console.log("Error in getting cargo insurance: ", error);
          return null;
        }
      })
    );
  };
  createCommodityObject = (commodity, commodityBase) => {
    const selectedCategory = commodity?.category
      ? this.props.commodityList.find(
          (item) => item.id === commodity.category.value
        )
      : {};
    return {
      ...commodityBase,
      commodity_description: commodity?.description,
      commodity_category_type: selectedCategory?.commodity_type,
      commodity_category_type_id: selectedCategory?.id,
      commodity_value: commodity?.commodityValue,
      profit_percent: INSURANCE_PROFIT_PERCENT,
      shipment_id: uuidv4(),
    };
  };

  handleInsuranceResult = (result, commodityObj) => {
    if (!(result?.[0] && result?.[1])) {
      this.props.updateErrorAndLoading(
        "Unable to retrieve the insurance quote at this time. Please try again shortly.",
        false
      );
      return null;
    }

    const quote = result[1];
    if (!isQuoteCreated(quote)) {
      this.props.updateErrorAndLoading(
        `An instant rate was not available for ${commodityObj.commodity_category_type}. Insurance has been requested, and we will provide a quote shortly.`,
        false
      );
    }

    // Keep the insurer's RAW premium (the real cost) and the PF profit applied on the
    // quote object, and set premium_amount to the customer-facing total (raw + profit,
    // 10% with a $50 floor). The row, card total, booking INS charge and the persisted
    // record all read premium_amount → they bill the customer figure; raw_premium /
    // profit_amount ride alongside for the Insurance record + finance reconciliation
    // (#1 "-" fix + profit rule).
    const raw = extractPremium(quote);
    if (raw != null) {
      // Round raw to cents, compute the profit ONCE, and derive the customer total
      // from it so the three fields always reconcile: premium_amount - raw_premium
      // === profit_amount (no floating-point cent drift).
      const rawCents = Number(raw.toFixed(2));
      const profit = profitAmount(rawCents);
      quote.raw_premium = rawCents;
      quote.profit_amount = profit;
      quote.premium_amount = Number((rawCents + profit).toFixed(2));
    }

    return { ...commodityObj, quote };
  };

  /**
   * The updateEntityLocation function is called when the "Calculate Insurance" button is clicked,
   * and the user's entity is invalid
   */
  updateEntityLocation = async () => {
    const { shouldPromptForLocation, entityAddress, entityCity, entityCountry } = this.state;
    const isUserPresent = this.props.user.webuser?.userid !== null;

    // resolve immediately if we do not need to prompt
    if (!shouldPromptForLocation || !isUserPresent) return true;

    if (shouldPromptForLocation && !(entityAddress && entityCity && entityCountry)){
      // Surface the required-field errors now — the customer has opted into getting a
      // quote, so the location is genuinely required at this point (and only now).
      this.setState({ locationValidateAttempted: true });
      this.props.updateErrorAndLoading("Please update your company's address, city, and country", false);
      throw new Error("Missing required location fields");
    }

    const locationFields = {
      address_1: entityAddress,
      city: entityCity,
      country: entityCountry.label,
      countryCode: entityCountry.value
    }

    try {
      await this.props.updateEntityLocationFromApi(locationFields, isUserPresent);
      // at this point, entity location has been updated successfully, so no longer render the form
      this.setState({ shouldPromptForLocation: false, locationValidateAttempted: false });
      return true;
    } catch (error){
      console.error(error);
      this.props.updateErrorAndLoading("Failed to update company location. Please try again", false);
      throw error;
    }
  };

  // Method to handle the insurance calculation button click
  calculateInsurance = () => {
    // A new calculation produces a new amount — drop any prior authorization so the
    // customer must re-tick against the figure they're about to be charged.
    this.setState({ policyAuthorized: false });
    this.updateEntityLocation().then(() => {

    const { form } = this.props;
    const values = form.getFieldsValue(["commodityArr"]);
  
    // Check if user has started filling any field in commodityArr
    const shouldValidate = values.commodityArr.some(
      (commodity) =>
        commodity?.category === undefined || commodity?.category === "" ||
        commodity?.description === undefined || commodity?.description === "" ||
        commodity?.commodityValue === undefined || commodity?.commodityValue === ""
    );
  
    if (shouldValidate) {
      // **Enable required validation**
      this.setState({ shouldValidate: true }, () => {
        let validationFields = [];
        values.commodityArr.forEach((commodity, index) => {
          validationFields.push(
            `commodityArr[${index}].category`,
            `commodityArr[${index}].description`,
            `commodityArr[${index}].commodityValue`
          );
        });
  
        // **Now enforce validation**
        form.validateFields(validationFields, { force: true }, (errors, values) => {
          if (!errors) {
            // console.log("Validated successfully:", values);
            this.handleSubmit(values);
          } else {
            console.error("Validation errors found: ", errors);
          }
        });
      });
    } else {
      // console.log("Skipping validation as no fields were entered.");
      this.handleSubmit(values);
    }
    }).catch((error) =>{
      console.error(error.message);
    });
  };

  processInsuranceQuotes = async (insurance, insuranceToken, isUserPresent) => {
    return Promise.all(
      insurance.map(async (insuranceElement) => {
        // console.log("insurance: ", insuranceElement);
        const quoteID = insuranceElement?.quote?.quote_id;
        const resultCode = insuranceElement?.quote?.result_code;
        if (resultCode === "quote_created" && quoteID) {
          //console.log("quoteID ", quoteID,"\ninsuranceToken: ",insuranceToken,"\nisUSerresent: ",isUserPresent);
          try {
            const result = await this.props.getInsurancePolicyFromAPI(
              isUserPresent,
              insuranceToken,
              quoteID
            );
            // console.log("Result: ", result);
            // Assuming result contains a policy_id field
            if (result[0] && result?.[1] && result[1]?.policy_id) {
              // Add policy_id to the insuranceElement
              insuranceElement.policy_id = result[1].policy_id;
              // console.log("Updated insurance element with policy_id: ", insuranceElement);
              // this.props.updateErrorAndLoading(`Policy has been cretaed for ${insuranceElement?.commodity_category_type}.`, false);
            }
          } catch (error) {
            console.log("Error in getting cargo insurance: ", error);
            return null;
          }
        } else {
          console.log(
            `Insurance Quote has not been generated for quoteId: ${quoteID}`
          );
        }
        return insuranceElement; // Return updated insurance element
      })
    );
  };
  createPolicy = async () => {
    const { insuranceToken, user } = this.props;
    const isUserPresent = user.webuser?.userid != null;
    console.log("createPolicy is executing.... ", this.state.insurance);
    if (this.state?.insurance && this.state.insurance.length > 0) {
      this.setState({ loadingPolicy: true });
      const updatedInsurance = await this.processInsuranceQuotes(
        this.state.insurance,
        insuranceToken,
        isUserPresent
      );
      // console.log("Updated insurance with policy_id: ", updatedInsurance);

      // Extract descriptions of elements that contain policy_id
      const descriptions = updatedInsurance
        .filter((item) => item && item.policy_id != null) // Check if policy_id is not null or undefined
        .map((item) => item.commodity_description) // Extract the description
        .join(", "); // Join descriptions into a comma-separated string

      if (descriptions) {
        this.props.updateErrorAndLoading(
          `Policy has been created for ${descriptions}.`,
          false
        );
      } else {
        this.props.updateErrorAndLoading(
          `Policy can not be created as quote has been requested.`,
          false
        );
      }
      // If you need to update the state with the updated insurance data
      this.setState({ insurance: updatedInsurance });
      this.props.onInsuranceAmountChange(updatedInsurance);
      this.setState({ loadingPolicy: false });
    }
  };

  render() {
    const {
      form,
      commodityList,
      commodityArr,
      onCommodityChange,
      onAddCommodity,
      onDeleteCommodity,
      user
    } = this.props;
    const { getFieldDecorator } = form;
    // Convert commodityList to the format required by react-select
    const options =
      commodityList && commodityList.length > 0
        ? commodityList.map((item) => ({
            value: item.id,
            label: item.commodity_type,
          }))
        : [];
    const isUserPresent = user.webuser?.userid != null;

    const insurance = this.state.insurance || [];
    // A policy can be created only once EVERY commodity row has a chargeable instant
    // premium (#2 "create policy only after calculating, all quoted").
    const allCommoditiesQuoted =
      insurance.length > 0 &&
      insurance.length === (commodityArr?.length || 0) &&
      insurance.every((item) => hasInstantPremium(item?.quote));
    // Marked-up total the customer authorizes — sums the normalized premium_amount.
    // Shown pre-policy (unlike the booking Total Price, which only counts created
    // policies).
    const calculatedTotal = insurance.reduce((sum, item) => {
      const p = Number(item?.quote?.premium_amount);
      return sum + (Number.isFinite(p) ? p : 0);
    }, 0);
    const needsReview = insurance.length > 0 && !allCommoditiesQuoted;
    const canCreatePolicy = allCommoditiesQuoted && this.state.policyAuthorized;
    // "Done" only when EVERY quoted commodity has a policy — a partial/failed run
    // leaves the step at "create" so the user can retry the missing ones.
    const policyComplete =
      insurance.length > 0 && insurance.every((item) => item?.policy_id != null);
    // The Create Policy action is live only while there's something left to create.
    const createPolicyReady = canCreatePolicy && !policyComplete;
    // Drives the 3-step guide (Calculate → Authorize → Create policy) and which
    // single button is highlighted, so the order is unambiguous.
    let policyStep = 0; // 0 = calculate, 1 = authorize, 2 = create, 3 = done
    if (policyComplete) policyStep = 3;
    else if (canCreatePolicy) policyStep = 2;
    else if (allCommoditiesQuoted) policyStep = 1;
    const calculateIsPrimary = insurance.length === 0;

    return (
      <Spin
        spinning={this.state.loadingShouldPromptForLocation}
        tip="Loading..."
        size="large"
      >
      <Form>
        {commodityArr?.map((commodity, index) => (
          <Form.Item key={index}>
            <div key={index}>
              <Row>
                <Col xs={24} sm={24} md={24}>
                  <Form.Item
                    label={<span style={labelStyle}>Commodity Category</span>}
                    style={{ marginBottom: "0" }}
                  >
                    {getFieldDecorator(`commodityArr[${index}].category`, {
                      initialValue: resolveCategoryOption(
                        commodity.category,
                        commodityList
                      ),
                      rules: [
                        {
                          required: this.state.shouldValidate,
                          message: "Required",
                        },
                      ],
                    })(
                      <Select
                        name="category"
                        options={options}
                        placeholder="Select a commodity type"
                        isSearchable
                        onChange={(selectedOption) => {
                          onCommodityChange(
                            index,
                            "category",
                            selectedOption.value
                          );
                        }}
                        styles={customStyles}
                      />
                    )}
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={[{ xs: 8, sm: 16, md: 24, lg: 32 }, 16]}>
                <Col xs={24} sm={24} md={12}>
                  <Form.Item
                    label={
                      <span style={labelStyle}>Commodity Description</span>
                    }
                    style={{ marginBottom: "0" }}
                  >
                    {getFieldDecorator(`commodityArr[${index}].description`, {
                      initialValue: commodity?.description || "",
                      rules: [
                        {
                          required: this.state.shouldValidate,
                          message: "required",
                        },
                      ],
                    })(
                      <Input
                        name="description"
                        size="large"
                        autoComplete="off"
                        placeholder="Describe the commodity"
                        onChange={(e) => {
                          onCommodityChange(
                            index,
                            "description",
                            e.target.value
                          );
                        }}
                      />
                    )}
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={12}>
                  <Form.Item
                    label={<span style={labelStyle}>Commodity Value</span>}
                    style={{ marginBottom: "0" }}
                  >
                    {getFieldDecorator(
                      `commodityArr[${index}].commodityValue`,
                      {
                        initialValue: commodity?.commodityValue || "",
                        rules: [
                          {
                            required: this.state.shouldValidate,
                            message: "required",
                          },
                          {
                            pattern: /^\d+(\.\d+)?$/, // Regular expression to accept integers or decimal numbers
                            message: "Please enter a valid price",
                          },
                        ],
                      }
                    )(
                      <Input
                        name="commodityValue"
                        size="large"
                        autoComplete="off"
                        placeholder="Enter amount"
                        addonBefore={
                          <span className="input-prefix">$ USD</span>
                        }
                        onChange={(e) => {
                          onCommodityChange(
                            index,
                            "commodityValue",
                            e.target.value
                          );
                        }}
                      />
                    )}
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                {/* Other input fields */}
                <Col xs={24} sm={24} md={6}>
                  <div style={{ display: "flex", paddingTop: "1rem" }}>
                    <Button
                      type="primary"
                      onClick={onAddCommodity}
                      style={{
                        width: "100%",
                        marginRight: commodityArr.length > 1 ? "10px" : "0px",
                      }}
                      ghost
                    >
                      <Icon type="plus" /> Add
                    </Button>
                    {commodityArr.length > 1 && (
                      <Button
                        type="danger"
                        // Removing a row changes commodityArr → componentDidUpdate
                        // invalidates the now-stale quotes (clears insurance + auth).
                        onClick={() => onDeleteCommodity(index)}
                        style={{ width: "100%" }}
                        ghost
                      >
                        <Icon type="minus" /> Delete
                      </Button>
                    )}
                  </div>
                </Col>
              </Row>
            </div>
          </Form.Item>
        ))}
        {!this.state.loadingShouldPromptForLocation && this.state.shouldPromptForLocation && (
          <div style={{ marginTop: "2rem", borderTop: "1px solid #eee", paddingTop: "1rem" }}>
            <h3>Update Your Company's Location</h3>

            <Form.Item
              label="Address"
              required
              validateStatus={this.state.locationValidateAttempted && !this.state.entityAddress ? "error" : ""}
              help={this.state.locationValidateAttempted && !this.state.entityAddress ? "Address is required" : ""}
            >
              <Input
                value={this.state.entityAddress}
                onChange={(e) => this.setState({ entityAddress: e.target.value })}
                placeholder="Enter your company's address"
              />
            </Form.Item>

            <Form.Item
              label="City"
              required
              validateStatus={this.state.locationValidateAttempted && !this.state.entityCity ? "error" : ""}
              help={this.state.locationValidateAttempted && !this.state.entityCity ? "City is required" : ""}
            >
              <Input
                value={this.state.entityCity}
                onChange={(e) => this.setState({ entityCity: e.target.value })}
                placeholder="Enter your company's city"
              />
            </Form.Item>

            <Form.Item
              label="Country"
              required
              validateStatus={this.state.locationValidateAttempted && !this.state.entityCountry ? "error" : ""}
              help={this.state.locationValidateAttempted && !this.state.entityCountry ? "Country is required" : ""}
            >
              <Select
                options={countryList().getData()}
                placeholder="Select your company's country"
                styles={customStyles}
                onChange={(selected) => this.setState({ entityCountry: selected })}
              />
            </Form.Item>
          </div>
        )}
        {isUserPresent && (
          <Steps
            size="small"
            current={policyStep}
            // Stack each step's description under its title so the 3rd step
            // ("Create policy") doesn't overflow / clip in the narrow booking column.
            labelPlacement="vertical"
            style={{ marginBottom: "1.25rem" }}
          >
            <Step title="Calculate" description="Get your premium" />
            <Step title="Authorize" description="Approve the charge" />
            <Step title="Create policy" description="Lock in coverage" />
          </Steps>
        )}
        <Row gutter={[{ xs: 8, sm: 16, md: 24, lg: 32 }, 16]}>
          <Col xs={24} sm={12} md={12} lg={12}>
            <Button
              type={calculateIsPrimary ? "primary" : "default"}
              onClick={this.calculateInsurance}
              disabled={this.state.loading || this.state.loadingShouldPromptForLocation}
              style={{
                width: "100%",
                ...(calculateIsPrimary ? {} : { backgroundColor: "#ECECEC", color: "#000" }),
              }}
            >
              {calculateIsPrimary ? "1. Calculate Insurance" : "Recalculate"}{" "}
              {this.state.loading && (
                <Spin
                  style={{
                    marginLeft: "10px",
                    marginRight: "10px",
                  }}
                  indicator={
                    <Icon type="loading" style={{ fontSize: 24 }} spin />
                  }
                />
              )}
            </Button>
          </Col>
          {isUserPresent &&
          <Col xs={24} sm={12} md={12} lg={12}>
            <Button
              type={createPolicyReady ? "primary" : "default"}
              onClick={this.createPolicy}
              disabled={this.state.loadingPolicy || this.state.loadingShouldPromptForLocation || !createPolicyReady}
              className={createPolicyReady ? "insurance-create-policy-ready" : undefined}
              style={{
                width: "100%",
                ...(createPolicyReady ? {} : { backgroundColor: "#ECECEC", color: "#000" }),
              }}
            >
              {policyComplete ? "Policy Created ✓" : "3. Create Policy"}
              {this.state.loadingPolicy && (
                <Spin
                  style={{
                    marginLeft: "10px",
                    marginRight: "10px",
                  }}
                  indicator={
                    <Icon type="loading" style={{ fontSize: 24 }} spin />
                  }
                />
              )}
            </Button>
          </Col>
        }
        </Row>
        {isUserPresent && needsReview && (
          <p style={{ color: "#cf6a00", marginTop: "0.75rem" }}>
            Some commodities need broker review — request competitive quotes via the
            “Get competing broker quotes (RFQ)” option under Additional services below.
          </p>
        )}
        {isUserPresent && allCommoditiesQuoted && (
          <div
            style={{
              marginTop: "0.75rem",
              padding: "0.5rem 0.75rem",
              borderRadius: 4,
              border: policyStep === 1 ? "1px solid #1890ff" : "1px solid transparent",
              background: policyStep === 1 ? "#e6f7ff" : "transparent",
              transition: "background 0.3s, border-color 0.3s",
            }}
          >
            <Checkbox
              checked={this.state.policyAuthorized}
              onChange={(e) =>
                this.setState({ policyAuthorized: e.target.checked })
              }
            >
              <strong>2.</strong> I authorize Prime Freight to charge me $
              {calculatedTotal.toFixed(2)} for cargo insurance.
            </Checkbox>
          </div>
        )}
        {isUserPresent &&
        <div>
          <p>
            <span style={{ color: "red" }}>* </span>An insurance policy must be
            created in order to obtain coverage.
          </p>
        </div>
        }
      </Form>
    </Spin>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    jwt_token: state.route.JWT_TOKEN.JWT_TOKEN,
    user: state.route.USER_DETAILS,
    queryParams: state.route.queryParams,
    final_search_form: state.route.final_search_form,
    commodityList: state.route.commodityList,
    insuranceToken: state.route.insuranceToken,
    booking_customer_form: state.route.booking_customer_form,
  };
};

export default withRouter(connect(mapStateToProps, actions)(CargoInsurance));
